/**
 * Routing utilities for path analysis and route classification
 *
 * Responsible for determining route types and their properties
 */

/**
 * Determines if a route is affected by logements synchronization
 *
 * Sync-affected routes: /, /bailleurs, /{bailleurId}, /{bailleurId}/logements/*, etc.
 * These routes display data from IndexedDB that depends on the sync completion.
 *
 * During sync, affected routes show SyncProgressSkeleton with progress bar.
 * Non-affected routes remain fully accessible and show their normal content.
 *
 * @param pathname - Current URL pathname
 * @returns true if route is affected by logements sync
 */
export const isSyncAffectedRoute = (pathname: string): boolean => {
  // Normalize pathname by removing trailing slash
  const normalizedPath = pathname.endsWith('/') && pathname.length > 1
    ? pathname.slice(0, -1)
    : pathname;

  // Routes NOT affected by sync (always accessible during sync)
  if (isAccountRoute(normalizedPath)) {
    return false;
  }

  // All other authenticated routes are sync-affected:
  // /, /bailleurs, /{bailleurId}, /{bailleurId}/logements/*, etc.
  return true;
};

/**
 * Determines if a route is an account route
 *
 * Account routes: /account/login, /account/logout, /account/me, etc.
 * These are typically public or semi-public routes that don't depend on logements sync.
 *
 * @param pathname - Current URL pathname
 * @returns true if route is an account-related route
 */
export const isAccountRoute = (pathname: string): boolean => {
  return pathname.startsWith('/account');
};

/**
 * Determines if a route is a public route (not requiring authentication)
 *
 * @param pathname - Current URL pathname
 * @returns true if route is public
 */
export const isPublicRoute = (pathname: string): boolean => {
  const publicRoutes = ['/account/login', '/account/forgot-password', '/account/reset-password'];
  return publicRoutes.some(route => pathname.startsWith(route));
};
